import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { type: "private", name: "", email: "", phone: "", vat: "", fiscal_code: "", address: "", city: "", codice_ipa: "", rup: "", contact_person: "", notes: "" };

export default function ClientEditDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({ ...emptyForm, ...client });
    } else {
      setForm(emptyForm);
    }
  }, [client?.id, open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        vat: form.vat || null,
        fiscal_code: form.fiscal_code || null,
        address: form.address || null,
        city: form.city || null,
        codice_ipa: form.codice_ipa || null,
        rup: form.rup || null,
        contact_person: form.contact_person || null,
        notes: form.notes || null,
      };
      if (client?.id) {
        await api.put(`/clients/${client.id}`, payload);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente creato");
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{client?.id ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="flex gap-2">
            {["private", "pa"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`edit-type-${t}`}
                onClick={() => setForm({ ...form, type: t })}
                className={`px-4 py-2 text-sm border ${form.type === t ? "brand-bg text-white border-transparent" : "border-neutral-300 text-neutral-700"}`}
              >
                {t === "private" ? "Cliente Privato" : "Pubblica Amministrazione"}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{form.type === "pa" ? "Denominazione Ente" : "Ragione sociale / Nome"} *</Label><Input data-testid="edit-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email / PEC</Label><Input type="email" data-testid="edit-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefono</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>P. IVA</Label><Input value={form.vat || ""} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
            <div><Label>Codice Fiscale</Label><Input value={form.fiscal_code || ""} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} /></div>
            <div><Label>Città</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="col-span-2"><Label>Indirizzo</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            {form.type === "pa" && (
              <>
                <div><Label>Codice IPA</Label><Input value={form.codice_ipa || ""} onChange={(e) => setForm({ ...form, codice_ipa: e.target.value })} /></div>
                <div><Label>RUP</Label><Input data-testid="edit-rup" value={form.rup || ""} onChange={(e) => setForm({ ...form, rup: e.target.value })} /></div>
              </>
            )}
            <div className="col-span-2"><Label>Referente</Label><Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div className="col-span-2"><Label>Note</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <Button type="submit" disabled={saving} data-testid="edit-save" className="brand-bg hover:opacity-90 w-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : client?.id ? "Salva modifiche" : "Crea cliente"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
